import { Suspense, lazy, useMemo, useState } from "react";
import type { CoverageCheckDto, DeliveryZoneDto, DeliveryZoneInput } from "@school-sales-ai/shared";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  useCheckCoverage,
  useCreateDeliveryZone,
  useDeliveryZones,
  useSetDeliveryZoneActive,
  useUpdateDeliveryZone,
} from "@/hooks/useDeliveryZones";
import { formatMxn } from "@/lib/format-mxn";
import { DeliveryZoneEditorDialog } from "./DeliveryZoneEditorDialog";
import type { BackgroundZone } from "./ZoneMap";
import { zoneColor } from "./zone-form";

const ZoneMap = lazy(() => import("./ZoneMap").then((m) => ({ default: m.ZoneMap })));

type EditorState = { open: false } | { open: true; zone: DeliveryZoneDto | null };

/**
 * "Zonas de entrega" in Agente → Ajustes: where a shop draws how far it
 * delivers and what delivery costs in each area.
 *
 * Saves on its own, per action — like the logo — so it carries no dirty state
 * for the page's Guardar button. Rendered only for admins of a shop with orders;
 * the routes refuse everyone else anyway.
 */
export function DeliveryZonesSettings(): React.JSX.Element {
  const { data, isLoading } = useDeliveryZones({ enabled: true });
  const create = useCreateDeliveryZone();
  const update = useUpdateDeliveryZone();
  const setActive = useSetDeliveryZoneActive();
  const check = useCheckCoverage();

  const [editor, setEditor] = useState<EditorState>({ open: false });
  const [deactivating, setDeactivating] = useState<DeliveryZoneDto | null>(null);
  const [testQuery, setTestQuery] = useState("");

  const zones = useMemo(() => data?.zones ?? [], [data]);
  const geocodingConfigured = data?.geocodingConfigured ?? true;
  // A zone keeps its colour while it exists — derived from its position in the
  // list the server returns (oldest first), the same on the map and in the list.
  const colors = useMemo(
    () => new Map(zones.map((z, i) => [z.id, zoneColor(i)])),
    [zones],
  );
  const activeZones = zones.filter((z) => z.isActive);
  const background = (exceptId: string | null): BackgroundZone[] =>
    activeZones
      .filter((z) => z.id !== exceptId)
      .map((z) => ({
        id: z.id,
        label: `${z.name} · ${formatMxn(z.feeCents)}`,
        geometry: z.geometry,
        color: colors.get(z.id)?.stroke ?? zoneColor(0).stroke,
      }));

  const save = (input: DeliveryZoneInput): void => {
    if (!editor.open) return;
    const close = { onSuccess: () => setEditor({ open: false }) };
    if (editor.zone) update.mutate({ id: editor.zone.id, input }, close);
    else create.mutate(input, close);
  };

  const runTest = (): void => {
    const query = testQuery.trim();
    if (query) check.mutate(query);
  };

  return (
    <div className="border-t border-ink-200 pt-4 mt-4">
      <div className="mb-1">
        <p className="text-[13px] font-medium text-ink-900">Zonas de entrega</p>
        <p className="text-[12px] text-ink-500 mt-0.5">
          Dibuja en el mapa hasta dónde entregas y cuánto cuesta el envío en cada zona. El agente
          verifica la dirección de cada cliente — calle, colonia, código postal o su ubicación de
          WhatsApp — contra estas zonas, en lugar de pedirle que elija de una lista. El costo de
          envío de cada pedido es el de su zona; sin zonas activas, los pedidos no cobran envío.
        </p>
      </div>

      {!geocodingConfigured && (
        <p className="mt-2 rounded-lg bg-amber-50 p-3 text-[12px] text-amber-800">
          La verificación de direcciones todavía no está configurada en el servidor. Puedes dibujar
          tus zonas, pero el agente seguirá usando la base de conocimiento hasta que se configure.
        </p>
      )}

      {isLoading ? (
        <div className="mt-3 h-24 rounded-lg bg-ink-050" />
      ) : (
        <>
          {activeZones.length > 0 && (
            <Suspense fallback={<div className="mt-3 h-56 rounded-lg bg-ink-050" />}>
              <ZoneMap
                className="mt-3 h-56 w-full rounded-lg border border-ink-200"
                backgroundZones={background(null)}
              />
            </Suspense>
          )}

          <ul className="mt-3 divide-y divide-ink-100 rounded-lg border border-ink-200">
            {zones.length === 0 && (
              <li className="px-3 py-3 text-[12px] text-ink-500">
                Todavía no hay zonas. Mientras no actives ninguna, el agente revisa la cobertura en
                la base de conocimiento.
              </li>
            )}
            {zones.map((zone) => (
              <li key={zone.id} className="flex items-center gap-3 px-3 py-2">
                <span
                  aria-hidden
                  className={`h-2.5 w-2.5 flex-shrink-0 rounded-full ${
                    zone.isActive ? (colors.get(zone.id)?.dot ?? "bg-sky-600") : "bg-ink-200"
                  }`}
                />
                <span className={`flex-1 text-[13px] ${zone.isActive ? "text-ink-900" : "text-ink-400"}`}>
                  {zone.name}
                  {!zone.isActive && <span className="ml-2 text-[11px]">(inactiva)</span>}
                </span>
                <span className="text-[13px] text-ink-700">
                  {zone.feeCents === 0 ? "Envío gratis" : formatMxn(zone.feeCents)}
                </span>
                <button
                  type="button"
                  onClick={() => setEditor({ open: true, zone })}
                  className="text-[12px] font-semibold text-moss"
                >
                  Editar
                </button>
                {zone.isActive ? (
                  <button
                    type="button"
                    onClick={() => setDeactivating(zone)}
                    className="text-[12px] font-semibold text-ink-500"
                  >
                    Desactivar
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={() => setActive.mutate({ id: zone.id, isActive: true })}
                    disabled={setActive.isPending}
                    className="text-[12px] font-semibold text-moss disabled:opacity-40"
                  >
                    Reactivar
                  </button>
                )}
              </li>
            ))}
          </ul>

          <button
            type="button"
            onClick={() => setEditor({ open: true, zone: null })}
            className="mt-3 rounded-[10px] bg-ink-100 px-3 py-1.5 text-[13px] font-semibold text-ink-700"
          >
            + Agregar zona
          </button>

          {activeZones.length > 0 && (
            <div className="mt-4">
              <p className="text-[13px] font-medium text-ink-900">Probar una dirección</p>
              <p className="text-[12px] text-ink-500 mt-0.5">
                Escribe una dirección como la escribiría un cliente y ve qué decidiría el agente.
              </p>
              <div className="mt-2 flex gap-2">
                <input
                  type="text"
                  value={testQuery}
                  onChange={(e) => setTestQuery(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      runTest();
                    }
                  }}
                  disabled={!geocodingConfigured}
                  maxLength={300}
                  placeholder="Av. Universidad 123, Centro"
                  aria-label="Dirección de prueba"
                  className="flex-1 rounded-lg border border-ink-200 px-3 py-1.5 text-[13px] text-ink-900 focus:border-moss focus:outline-none disabled:bg-ink-050"
                />
                <button
                  type="button"
                  onClick={runTest}
                  disabled={!geocodingConfigured || check.isPending || !testQuery.trim()}
                  className="rounded-lg bg-ink-100 px-3 py-1.5 text-[13px] font-semibold text-ink-700 disabled:opacity-40"
                >
                  {check.isPending ? "Revisando…" : "Probar"}
                </button>
              </div>
              {check.data && <CoverageResult result={check.data} />}
            </div>
          )}
        </>
      )}

      {editor.open && (
        <DeliveryZoneEditorDialog
          zone={editor.zone}
          otherZones={background(editor.zone?.id ?? null)}
          color={
            editor.zone
              ? (colors.get(editor.zone.id)?.stroke ?? zoneColor(0).stroke)
              : zoneColor(zones.length).stroke
          }
          geocodingConfigured={geocodingConfigured}
          saving={create.isPending || update.isPending}
          onCancel={() => setEditor({ open: false })}
          onSave={save}
        />
      )}

      <AlertDialog open={deactivating !== null} onOpenChange={(v: boolean) => !v && setDeactivating(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Desactivar «{deactivating?.name}»?</AlertDialogTitle>
            <AlertDialogDescription>
              El agente dejará de entregar en esa zona: a quien escriba desde ahí le dirá que no
              hay cobertura. Los pedidos que ya la usaron no cambian, y puedes reactivarla cuando
              quieras.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={setActive.isPending}>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              disabled={setActive.isPending}
              onClick={() => {
                if (!deactivating) return;
                setActive.mutate(
                  { id: deactivating.id, isActive: false },
                  { onSettled: () => setDeactivating(null) },
                );
              }}
            >
              Desactivar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

/** The tester's answer, in the words the agent would use. */
export function CoverageResult({ result }: { result: CoverageCheckDto }): React.JSX.Element {
  const where = result.formattedAddress ? (
    <span className="text-ink-500"> — entendimos «{result.formattedAddress}»</span>
  ) : null;
  if (result.status === "covered") {
    return (
      <p className="mt-2 text-[12px] text-emerald-700">
        Sí hay entrega: zona {result.zoneName},{" "}
        {result.feeCents === 0 ? "envío gratis" : `envío ${formatMxn(result.feeCents)}`}
        {result.precision === "area" ? " (es una zona aproximada; al cerrar se pide calle y número)" : ""}
        .{where}
      </p>
    );
  }
  if (result.status === "not_covered") {
    return <p className="mt-2 text-[12px] text-red-600">Fuera de cobertura.{where}</p>;
  }
  if (result.status === "ambiguous") {
    return (
      <p className="mt-2 text-[12px] text-amber-700">
        Queda en el límite de una zona: el agente le pedirá calle y número o su ubicación.{where}
      </p>
    );
  }
  return (
    <p className="mt-2 text-[12px] text-ink-500">
      No encontramos esa dirección: el agente le pedirá que la escriba de otra forma.
    </p>
  );
}
